// Pitcher Rest Day Warning utility
// Scans a team's recent games and returns per-pitcher rest-day status
import { REST_DAYS } from '../components/shared/PitchCountBadge';

// Determine if a team enforces Little-League-style pitch rest rules.
// Explicit team.enforce_pitch_rest_rules wins; otherwise default: baseball=true, softball=false.
export const isRestEnforced = (team) => {
  if (!team) return true;
  if (team.enforce_pitch_rest_rules === true || team.enforce_pitch_rest_rules === false) {
    return team.enforce_pitch_rest_rules;
  }
  return (team.sport_type || 'baseball').toLowerCase() !== 'softball';
};

const getRestDaysRequired = (pitchCount) => {
  const rule = REST_DAYS.find(r => pitchCount >= r.min && pitchCount <= r.max);
  return rule ? rule.days : 0;
};

// Parse a 'YYYY-MM-DD' string as a local date (midnight)
const parseGameDate = (dateStr) => {
  if (!dateStr) return null;
  // support ISO or YYYY-MM-DD
  const [y, m, d] = dateStr.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const daysBetween = (from, to) => {
  const ms = to.setHours(0, 0, 0, 0) - from.setHours(0, 0, 0, 0);
  return Math.round(ms / (1000 * 60 * 60 * 24));
};

/**
 * Build a map of pitcherId -> rest status from the team's recent games.
 * @param {Array} games - list of games (each with `date`, `pitcher_appearance_log`, `pitching_history`)
 * @param {string} currentGameId - id of the game currently being played (excluded from history)
 * @returns {Object} { [pitcherId]: { lastPitchCount, daysSince, restRequired, needsRest, lastGameDate } }
 */
export const buildRestMap = (games, currentGameId = null) => {
  const today = new Date();
  const map = {};

  const consider = (pitcherId, pitches, gameDate) => {
    if (!pitcherId || !pitches || pitches < 1 || !gameDate) return;
    const daysSince = daysBetween(new Date(gameDate), new Date(today));
    if (daysSince < 0 || daysSince > 6) return; // only consider last 6 days
    const prev = map[pitcherId];
    // Keep the most recent outing that could trigger a warning
    if (!prev || daysSince < prev.daysSince) {
      const restRequired = getRestDaysRequired(pitches);
      map[pitcherId] = {
        lastPitchCount: pitches,
        daysSince,
        restRequired,
        needsRest: daysSince < restRequired,
        lastGameDate: gameDate,
      };
    }
  };

  (games || []).forEach(g => {
    if (currentGameId && g.id === currentGameId) return;
    const gDate = parseGameDate(g.date);
    if (!gDate) return;

    // Sum pitches per pitcher from pitcher_appearance_log (non-opponent only)
    const totals = {};
    (g.pitcher_appearance_log || []).forEach(e => {
      if (e.is_opponent) return;
      if (!e.pitcher_id) return;
      totals[e.pitcher_id] = (totals[e.pitcher_id] || 0) + (e.pitch_count || 0);
    });
    // Legacy pitching_history (our team only)
    (g.pitching_history || []).forEach(e => {
      if (!e.pitcher_id) return;
      totals[e.pitcher_id] = (totals[e.pitcher_id] || 0) + (e.pitch_count || 0);
    });

    Object.entries(totals).forEach(([pid, pc]) => consider(pid, pc, gDate));
  });

  return map;
};

/**
 * Build a human-readable warning string for a pitcher (null if no warning).
 */
export const getRestWarning = (restMap, pitcherId) => {
  const entry = restMap?.[pitcherId];
  if (!entry || !entry.needsRest) return null;
  const daysLeft = entry.restRequired - entry.daysSince;
  const outingWhen = entry.daysSince === 0 ? 'today' : entry.daysSince === 1 ? 'yesterday' : `${entry.daysSince} days ago`;
  return `Threw ${entry.lastPitchCount} pitches ${outingWhen} — needs ${entry.restRequired} rest day${entry.restRequired === 1 ? '' : 's'} (${daysLeft} more).`;
};

/**
 * Per-pitcher availability status for a pre-game overview card.
 * Returns { status: 'ready'|'caution'|'locked', label, detail, daysLeft }
 * - ready: no recent outing or already rested.
 * - caution: needs exactly 1 more rest day (likely available next game).
 * - locked: needs 2+ more rest days.
 */
export const getPitcherStatus = (restMap, pitcherId) => {
  const entry = restMap?.[pitcherId];
  if (!entry || !entry.needsRest) {
    if (entry) {
      const outingWhen = entry.daysSince === 0 ? 'today' : entry.daysSince === 1 ? 'yesterday' : `${entry.daysSince} days ago`;
      return { status: 'ready', label: 'Ready', detail: `${entry.lastPitchCount} pitches ${outingWhen} · rested`, daysLeft: 0 };
    }
    return { status: 'ready', label: 'Ready', detail: 'No recent outing', daysLeft: 0 };
  }
  const daysLeft = entry.restRequired - entry.daysSince;
  const outingWhen = entry.daysSince === 0 ? 'today' : entry.daysSince === 1 ? 'yesterday' : `${entry.daysSince} days ago`;
  const detail = `${entry.lastPitchCount} pitches ${outingWhen} · ${daysLeft} more rest day${daysLeft === 1 ? '' : 's'}`;
  return {
    status: daysLeft === 1 ? 'caution' : 'locked',
    label: daysLeft === 1 ? '1 Day' : 'Locked',
    detail,
    daysLeft,
  };
};

const ymdKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Build a daily pitch history for a specific pitcher across the last `days` days.
 * Returns array of { dateKey, date, pitches, tier, restRequired } newest last.
 * Tiers align with REST_DAYS thresholds so color bucketing is consistent.
 */
export const buildPitcherDailyLoad = (games, pitcherId, days = 14) => {
  const totals = {};
  (games || []).forEach(g => {
    const dateStr = g.date;
    if (!dateStr || !dateStr.slice) return;
    const key = dateStr.slice(0, 10);
    (g.pitcher_appearance_log || []).forEach(e => {
      if (e.is_opponent) return;
      if (e.pitcher_id !== pitcherId) return;
      totals[key] = (totals[key] || 0) + (e.pitch_count || 0);
    });
    (g.pitching_history || []).forEach(e => {
      if (e.pitcher_id !== pitcherId) return;
      totals[key] = (totals[key] || 0) + (e.pitch_count || 0);
    });
  });

  const tierFor = (pc) => {
    if (!pc) return { tier: 'none', restRequired: 0 };
    const rule = REST_DAYS.find(r => pc >= r.min && pc <= r.max);
    const rest = rule ? rule.days : 0;
    let tier;
    if (pc >= 66) tier = 'max';
    else if (pc >= 51) tier = 'heavy';
    else if (pc >= 36) tier = 'moderate';
    else if (pc >= 21) tier = 'low';
    else tier = 'rest-light';
    return { tier, restRequired: rest };
  };

  const out = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = ymdKey(d);
    const pc = totals[key] || 0;
    const { tier, restRequired } = tierFor(pc);
    out.push({ dateKey: key, date: d, pitches: pc, tier, restRequired });
  }
  return out;
};
